import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/app/routes';
import { Table, ButtonVariants, BadgeVariants } from '@carboledger/ui';
import { Plus } from '@carboledger/ui/icons';
import { useTranslations, UnitTypeContext, useUnitTypeSelection } from '@/hooks';
import { ConfirmDialog } from '@/components/feedback';
import { DiscrepancyModal } from '@/components/common';
import { buildIncomingShipmentFilterDefinitions } from '@/config/filterDefinitions';
import { buildTableLabels } from '@/config/tableLabels';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import {
  buildLinkedShipmentsColumns,
  getShipmentStatusVariantMap,
} from '../constants/contractLinkedShipmentsColumns';
import { useUnlinkContractShipmentMutation } from '../api/contractsApi';

const getLinkedShipmentId = (row) => row.id;

/**
 * Props:
 *   module              the active module route segment
 *   contractId          the contract's database id
 *   contractBusinessId  the contract's displayed id
 *   linkedShipments     the linked-shipment rows
 *   onFilterChange      (filters) => void — re-reads the tab
 */
export const LinkedShipmentsTabView = ({
  module,
  contractId,
  contractBusinessId,
  linkedShipments,
  onFilterChange,
}) => {
  const t = useTranslations();
  const navigate = useNavigate();
  const [shipmentDiscrepancyRow, setShipmentDiscrepancyRow] = useState(null);
  const [removeLinkageRow, setRemoveLinkageRow] = useState(null);
  const [unlinkContractShipment] = useUnlinkContractShipmentMutation();
  const { selections: unitSelections, contextValue: unitTypeContext } = useUnitTypeSelection();
  const filterDefinitions = useMemo(() => buildIncomingShipmentFilterDefinitions(t), [t]);
  const tableLabels = useMemo(() => buildTableLabels(t), [t]);
  const handleFilterChange = useCallback((values) => onFilterChange?.(values), [onFilterChange]);

  const handleAddDeclaration = useCallback(
    (row) => {
      navigate(ROUTES.incomingShipmentAddDeclarations(module, row.shipmentDbId));
    },
    [navigate, module],
  );
  const handleRemoveLinkage = useCallback((row) => setRemoveLinkageRow(row), []);
  const handleCancelRemoveLinkage = useCallback(() => setRemoveLinkageRow(null), []);
  const handleConfirmRemoveLinkage = useCallback(async () => {
    await unlinkContractShipment({
      module,
      id: contractId,
      shipmentId: removeLinkageRow.shipmentDbId,
    });
    setRemoveLinkageRow(null);
  }, [unlinkContractShipment, module, contractId, removeLinkageRow]);
  // `shipmentDbId`, not the link row's id — the route keys on the shipment itself.
  const handleViewLinkedShipment = useCallback(
    (row) => {
      navigate(ROUTES.incomingShipmentDetail(module, row.shipmentDbId));
    },
    [navigate, module],
  );
  const handleLinkIncomingShipments = useCallback(() => {
    // TODO: open the link-incoming-shipments flow once it exists.
  }, []);
  const handleOpenShipmentDiscrepancy = useCallback((row) => setShipmentDiscrepancyRow(row), []);
  const handleCloseShipmentDiscrepancy = useCallback(() => setShipmentDiscrepancyRow(null), []);

  const columns = useMemo(
    () => buildLinkedShipmentsColumns(t, { onOpenDiscrepancy: handleOpenShipmentDiscrepancy }),
    [t, handleOpenShipmentDiscrepancy],
  );

  const rowActions = useMemo(
    () => [
      {
        id: 'add-declaration',
        label: t('CONTRACTS_LINKED_SHIPMENTS_ACTION_ADD_DECLARATION'),
        variant: ButtonVariants.tertiary,
        onClick: handleAddDeclaration,
      },
      {
        id: 'remove-linkage',
        label: t('ACTION_REMOVE_LINKAGE'),
        variant: ButtonVariants.tertiary,
        onClick: handleRemoveLinkage,
      },
    ],
    [t, handleAddDeclaration, handleRemoveLinkage],
  );

  const rowMenuActions = useMemo(
    () => [
      {
        id: 'view',
        label: t('CONTRACTS_LINKED_SHIPMENTS_ACTION_VIEW'),
        onClick: handleViewLinkedShipment,
      },
    ],
    [t, handleViewLinkedShipment],
  );

  const emptyState = useMemo(
    () => ({
      image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
      title: t('CONTRACTS_DETAIL_SHIPMENTS_EMPTY_TITLE'),
      description: t('CONTRACTS_DETAIL_SHIPMENTS_EMPTY_DESCRIPTION'),
      primaryLabel: t('CONTRACTS_DETAIL_SHIPMENTS_EMPTY_CTA'),
      onPrimaryClick: handleLinkIncomingShipments,
      primaryIcon: <Plus />,
    }),
    [t, handleLinkIncomingShipments],
  );

  const shipmentDiscrepancies = shipmentDiscrepancyRow?.discrepancyItems ?? [];

  const shipmentDiscrepancySections = useMemo(() => {
    if (!shipmentDiscrepancyRow) return [];
    const selectedUnit =
      unitSelections.get(shipmentDiscrepancyRow.id) ?? shipmentDiscrepancyRow.unitType;
    const unitValues = shipmentDiscrepancyRow.byUnit?.[selectedUnit] ?? {};
    return [
      {
        title: t('SECTION_GENERAL_DETAILS'),
        fields: [
          {
            label: t('FIELD_SHIPMENT_ID'),
            value: shipmentDiscrepancyRow.shipmentId,
          },
          {
            label: t('CONTRACTS_LINKED_SHIPMENTS_COL_LINKED_CONTRACT'),
            value: shipmentDiscrepancyRow.linkedContract,
          },
          { label: t('FIELD_TERMINAL'), value: shipmentDiscrepancyRow.terminal },
          {
            label: t('CONTRACTS_LINKED_SHIPMENTS_COL_SHIPMENT_STATUS'),
            value: shipmentDiscrepancyRow.shipmentStatus,
            type: 'status',
            tone:
              getShipmentStatusVariantMap(t)[shipmentDiscrepancyRow.shipmentStatus] ??
              BadgeVariants.default,
          },
        ],
      },
      {
        title: t('SECTION_QUANTITY'),
        fields: [
          {
            label: t('CONTRACTS_LINKED_SHIPMENTS_COL_ASSIGNED_QTY'),
            value: unitValues.assignedQty,
          },
          {
            label: t('CONTRACTS_LINKED_SHIPMENTS_COL_ACTUAL_SHIPMENT_QTY'),
            value: unitValues.actualShipmentQty,
          },
          {
            label: t('FIELD_AVAILABLE_QUANTITY'),
            value: unitValues.availableQty,
          },
        ],
      },
    ];
  }, [shipmentDiscrepancyRow, unitSelections, t]);

  return (
    <>
      <UnitTypeContext.Provider value={unitTypeContext}>
        <Table
          columns={columns}
          data={linkedShipments ?? []}
          getRowId={getLinkedShipmentId}
          searchable
          labels={tableLabels}
          filters={filterDefinitions}
          onFilterChange={handleFilterChange}
          rowActions={rowActions}
          rowMenuActions={rowMenuActions}
          actionsHeader={t('FIELD_ACTION')}
          emptyState={emptyState}
        />
      </UnitTypeContext.Provider>

      <DiscrepancyModal
        open={shipmentDiscrepancyRow != null}
        onClose={handleCloseShipmentDiscrepancy}
        referenceId={shipmentDiscrepancyRow?.shipmentId}
        discrepancies={shipmentDiscrepancies}
        sections={shipmentDiscrepancySections}
      />

      <ConfirmDialog
        open={removeLinkageRow != null}
        title={t('ACTION_REMOVE_LINKAGE')}
        bodyTitle={t('CONTRACTS_LINKED_SHIPMENTS_REMOVE_LINKAGE_BODY_TITLE', {
          shipmentId: removeLinkageRow?.shipmentId,
          contractId: contractBusinessId,
        })}
        description={t('REMOVE_LINKAGE_DESCRIPTION')}
        confirmLabel={t('COMMON_REMOVE')}
        tone="danger"
        onConfirm={handleConfirmRemoveLinkage}
        onCancel={handleCancelRemoveLinkage}
      />
    </>
  );
};
